import React, { ReactElement, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import {
  IN_NETWORK,
  IN_NETWORK_KEY,
  OUT_OF_NETWORK_KEY,
  TYPE_MEDICAID,
} from '../../../../../constants/index';
import { isEmptyObject, safeGet } from '../../../../../core/util/object';
import { getDiffBetweenDates } from '../../../../../core/util/date';
import {
  capitalCase,
  formatPercentage,
  formatPrice,
  pluralize,
} from '../../../../../core/util/string';
import { getDiffBetweenAppliedAndLimitValue } from './insuranceProgress';
import Odometer from '../../../../SolvPatternLibrary/Odometer';
import { isEmpty } from '../../../../../core/util/empty';
import { hasSufficientDataToDisplayInsuranceProfile } from '../../../../../core/util/account';
import { InsuranceBenefitStateTypes } from '../../../../../constants/insurance';

const AnimatedStat = styled(Odometer).withConfig({ componentId: 'sc-4v2482-0' })`
  display: inline-block;
`;

const NO_LIMIT = '0';

export const getInNetworkIndividualDeductible = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.deductible.individual.in_network');

export const getOutOfNetworkIndividualDeductible = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.deductible.individual.out_of_network');

export const getInNetworkFamilyDeductible = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.deductible.family.in_network');

export const getOutOfNetworkFamilyDeductible = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.deductible.family.out_of_network');

export const getInNetworkIndividualOutOfPocketMax = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.out_of_pocket.individual.in_network');

export const getOutOfNetworkIndividualOutOfPocketMax = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.out_of_pocket.individual.out_of_network');

export const getInNetworkFamilyOutOfPocketMax = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.out_of_pocket.family.in_network');

export const getOutOfNetworkFamilyOutOfPocketMax = (insuranceDetails: any) =>
  safeGet(insuranceDetails, null)('summary.out_of_pocket.family.out_of_network');

export const getMaximumCopay = (insuranceDetails: any, network = IN_NETWORK) => {
  if (!insuranceDetails.copay) return 0;

  const copays = insuranceDetails?.copay
    ?.filter((copay: any) => copay.in_network === network)
    .map((copay: any) => copay.amount);

  if (!copays) {
    return null;
  }

  if (copays.length === 0) {
    return 0;
  }

  return Math.max.apply(null, copays);
};

export const getMaximumCoinsurance = (insuranceDetails: any, network = IN_NETWORK) => {
  const coinsurance = insuranceDetails?.coinsurance
    ?.filter((coinsurance: any) => coinsurance.in_network === network)
    .map((coinsurance: any) => coinsurance.percent);

  if ((coinsurance?.length ?? 0) === 0) {
    return null;
  }

  return Math.max.apply(null, coinsurance);
};

export const getCopayCoinsurance = (insuranceDetails: any, providerType: any) =>
  safeGet(insuranceDetails)(`summary.copay_coinsurance.${providerType}`);

export const getCopay = (insuranceDetails: any, providerType: any, network: any) =>
  safeGet(insuranceDetails)(`summary.copay_coinsurance.${providerType}.${network}.copay`);

export const getCoinsurance = (insuranceDetails: any, providerType: any, network: any) =>
  safeGet(insuranceDetails)(`summary.copay_coinsurance.${providerType}.${network}.coinsurance`);

export const isActive = (insuranceDetails: any) =>
  !isEmptyObject(insuranceDetails) && !!insuranceDetails.active;

export const trackingProperties = (insuranceDetails: any) => {
  const deductible = getInNetworkIndividualDeductible(insuranceDetails);
  const hasRemainingDeductible = deductible && deductible.remaining > 0;
  return {
    has_insurance: isActive(insuranceDetails),

    plan_type: safeGet(insuranceDetails, null)('plan.type'),
    has_deductible_remaining: hasRemainingDeductible,
  };
};

export const isMedicaid = (insuranceDetails: any) => {
  const planType = safeGet(insuranceDetails)('plan.type');
  return planType && planType.toLowerCase() === TYPE_MEDICAID;
};

export const hasNoLimit = (limit: any) => limit === NO_LIMIT;

export const hasLimitBeenMet = (applied: any, limit: any) =>
  !hasNoLimit(limit) && parseInt(applied, 10) >= parseInt(limit, 10);

export const hasFamilyOopMax = (insuranceDetails: any) =>
  safeGet(insuranceDetails.summary)('out_of_pocket.family.in_network.limit') !== NO_LIMIT;

export const hasNoIndividualDeductible = (insuranceDetails: any) =>
  hasNoLimit(safeGet(insuranceDetails.summary)('deductible.individual.in_network.limit'));

export const hasNoFamilyDeductible = (insuranceDetails: any) =>
  hasNoLimit(safeGet(insuranceDetails.summary)('deductible.family.in_network.limit'));

export const hasHitIndividualDeductible = (insuranceDetails: any) => {
  return hasLimitBeenMet(
    safeGet(insuranceDetails.summary)('deductible.individual.in_network.applied'),
    safeGet(insuranceDetails.summary)('deductible.individual.in_network.limit')
  );
};

export const hasHitFamilyDeductible = (insuranceDetails: any) =>
  hasLimitBeenMet(
    safeGet(insuranceDetails.summary)('deductible.family.in_network.applied'),
    safeGet(insuranceDetails.summary)('deductible.family.in_network.limit')
  );

export const hasHitIndividualInNetworkOopMax = (insuranceDetails: any) =>
  hasLimitBeenMet(
    safeGet(insuranceDetails.summary)('out_of_pocket.individual.in_network.applied'),
    safeGet(insuranceDetails.summary)('out_of_pocket.individual.in_network.limit')
  );

export const hasHitIndividualOutNetworkOopMax = (insuranceDetails: any) =>
  hasLimitBeenMet(
    safeGet(insuranceDetails.summary)('out_of_pocket.individual.out_of_network.applied'),
    safeGet(insuranceDetails.summary)('out_of_pocket.individual.out_of_network.limit')
  );

export const hasHitFamilyOopMax = (insuranceDetails: any) =>
  hasLimitBeenMet(
    safeGet(insuranceDetails.summary)('out_of_pocket.family.in_network.applied'),
    safeGet(insuranceDetails.summary)('out_of_pocket.family.in_network.limit')
  );

const getMaxOfDeductibleAndOopMaxSpend = (appliedDeductible: any, appliedOopMax: any) =>
  Math.max(appliedDeductible, appliedOopMax);

const getStatEndCount = (insuranceDetails: any) => {
  const inNetworkIndividualDeductible = getInNetworkIndividualDeductible(insuranceDetails);
  const inNetworkIndividualOutOfPocketMax = getInNetworkIndividualOutOfPocketMax(insuranceDetails);
  if (!inNetworkIndividualDeductible && !inNetworkIndividualOutOfPocketMax) {
    return null;
  }
  if (!inNetworkIndividualDeductible && inNetworkIndividualOutOfPocketMax) {
    return parseInt(inNetworkIndividualDeductible.applied, 10);
  }

  if (inNetworkIndividualDeductible && !inNetworkIndividualOutOfPocketMax) {
    return parseInt(inNetworkIndividualOutOfPocketMax.applied, 10);
  }

  return getMaxOfDeductibleAndOopMaxSpend(
    parseInt(inNetworkIndividualDeductible.applied, 10),
    parseInt(inNetworkIndividualOutOfPocketMax.applied, 10)
  );
};

const getAmountSpentSinceDeductibleStartDateCopy = (
  insuranceDetails: any,
  deductibleStartDate: any,
  now: any
) => {
  const monthsSinceDeductibleStartDate = getDiffBetweenDates(deductibleStartDate, now, 'months');
  const startDateCopy = `${monthsSinceDeductibleStartDate} ${pluralize(
    monthsSinceDeductibleStartDate,
    'month'
  )} ago`;
  const endCount = getStatEndCount(insuranceDetails);
  if (!endCount && endCount !== 0) {
    return null;
  }

  return (
    <span>
      You've spent $
      <AnimatedStat animationDuration={1500} endCount={endCount} /> since your plan started{' '}
      {deductibleStartDate && startDateCopy}
    </span>
  );
};

const getMonthsUntilPlanExpiresCopy = (
  insuranceDetails: any,
  planStartDate: any,
  now: any,
  includeFamily = false
) => {
  const yearFromPlanStartDate = moment(planStartDate).add(1, 'year');
  const monthsUntilPlanExpires = getDiffBetweenDates(now, yearFromPlanStartDate, 'months');
  const copy = Number.isNaN(monthsUntilPlanExpires)
    ? `${
        includeFamily ? "You and your family's" : 'Your'
      } care is covered by insurance until your deductible resets.`
    : `For the next ${monthsUntilPlanExpires} ${pluralize(monthsUntilPlanExpires, 'month')}, ${
        includeFamily ? "you and your family's" : 'your'
      } care is covered by insurance.`;
  return <span>{copy}</span>;
};

const getAmountUntilDeductibleHitCopy = (applied: any, limit: any) =>
  `You're $${getDiffBetweenAppliedAndLimitValue(
    applied,
    limit
  )} away from hitting your deductible, and your benefits kicking in.`;

const getLimitHitCopy = (limit: any, isDeductible: any) => {
  return (
    <span>
      You hit your $
      <AnimatedStat endCount={parseInt(limit, 10)} /> individual{' '}
      {isDeductible ? 'deductible' : 'out-of-pocket maximum'}
    </span>
  );
};

const getBothLimitsHitCopy = (isDeductible: any) => {
  return (
    <span>
      You hit both your individual and family{' '}
      {isDeductible ? 'deductible' : 'out-of-pocket maximum'}.
    </span>
  );
};

const getInProgressIcon = (insuranceDetails: any) =>
  getStatEndCount(insuranceDetails) === 0
    ? '/images/insurance/empty.png'
    : '/images/insurance/progress.png';

type InsuranceHeadlineCopy = {
  title: ReactElement | null;
  subtext: ReactElement | string;
  icon: string;
} | null;

//TODO: reuse this headlinecopy function and others in this file - port over into a util file
export const getInsuranceHeadlineCopy = (insuranceDetails: any): InsuranceHeadlineCopy => {
  // This represents the loading state where insurance is still being retrieved, and causes
  // HeadlineBanner.tsx to display a loading spinner in place oof this copy
  if (insuranceDetails && isEmptyObject(insuranceDetails.summary)) return null;
  const now = moment();
  const deductibleStartDate = safeGet(insuranceDetails)('plan.deductible_start_date');
  const planStartDate = safeGet(insuranceDetails)('plan.start_date');
  const individualDeductibleData = getInNetworkIndividualDeductible(insuranceDetails);
  const individualOopMaxData = getInNetworkIndividualOutOfPocketMax(insuranceDetails);

  switch (true) {
    case !individualDeductibleData || !individualOopMaxData:
      return null;
    case !hasFamilyOopMax(insuranceDetails) &&
      !hasNoIndividualDeductible(insuranceDetails) &&
      !hasHitIndividualDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails):
      return {
        title: getAmountSpentSinceDeductibleStartDateCopy(
          insuranceDetails,
          deductibleStartDate,
          now
        ),
        subtext: getAmountUntilDeductibleHitCopy(
          individualDeductibleData.applied,
          individualDeductibleData.limit
        ),
        icon: getInProgressIcon(insuranceDetails),
      };

    case !hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails):
      return {
        title: getLimitHitCopy(individualDeductibleData.limit, true),
        subtext: 'Insurance is now chipping in for part of your healthcare costs.',
        icon: '/images/insurance/goal.png',
      };

    case !hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      hasHitIndividualInNetworkOopMax(insuranceDetails):
      return {
        title: getLimitHitCopy(individualOopMaxData.limit, false),
        subtext: getMonthsUntilPlanExpiresCopy(insuranceDetails, planStartDate, now),
        icon: '/images/insurance/goal.png',
      };

    case !hasFamilyOopMax(insuranceDetails) &&
      hasNoIndividualDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails):
      return {
        title: getAmountSpentSinceDeductibleStartDateCopy(
          insuranceDetails,
          deductibleStartDate,
          now
        ),
        subtext: 'Your plan has no deductible – insurance will always pay for part of your care.',
        icon: getInProgressIcon(insuranceDetails),
      };

    case !hasFamilyOopMax(insuranceDetails) &&
      hasNoIndividualDeductible(insuranceDetails) &&
      hasHitIndividualInNetworkOopMax(insuranceDetails):
      return {
        title: getLimitHitCopy(individualOopMaxData.limit, false),
        subtext: getMonthsUntilPlanExpiresCopy(insuranceDetails, planStartDate, now),
        icon: '/images/insurance/goal.png',
      };

    case hasFamilyOopMax(insuranceDetails) &&
      hasNoIndividualDeductible(insuranceDetails) &&
      hasNoFamilyDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      !hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getAmountSpentSinceDeductibleStartDateCopy(
          insuranceDetails,
          deductibleStartDate,
          now
        ),
        subtext: 'Your plan has no deductible – insurance will always pay for part of your care.',
        icon: getInProgressIcon(insuranceDetails),
      };

    case hasFamilyOopMax(insuranceDetails) &&
      !hasNoIndividualDeductible(insuranceDetails) &&
      !hasHitIndividualDeductible(insuranceDetails) &&
      !hasHitFamilyDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      !hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getAmountSpentSinceDeductibleStartDateCopy(
          insuranceDetails,
          deductibleStartDate,
          now
        ),
        subtext: getAmountUntilDeductibleHitCopy(
          individualDeductibleData.applied,
          individualDeductibleData.limit
        ),
        icon: getInProgressIcon(insuranceDetails),
      };

    case hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      !hasHitFamilyDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      !hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getLimitHitCopy(individualDeductibleData.limit, true),
        subtext: 'Insurance is now chipping in for part of your healthcare costs.',
        icon: '/images/insurance/goal.png',
      };

    case hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      hasHitFamilyDeductible(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      !hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getBothLimitsHitCopy(true),
        subtext: 'Insurance is now chipping in for part of your healthcare costs.',
        icon: '/images/insurance/goal.png',
      };

    case hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      hasHitFamilyDeductible(insuranceDetails) &&
      hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      !hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getLimitHitCopy(individualOopMaxData.limit, false),
        subtext: getMonthsUntilPlanExpiresCopy(insuranceDetails, planStartDate, now),
        icon: '/images/insurance/goal.png',
      };

    case hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      hasHitFamilyDeductible(insuranceDetails) &&
      hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getBothLimitsHitCopy(false),
        subtext: getMonthsUntilPlanExpiresCopy(insuranceDetails, planStartDate, now, true),
        icon: '/images/insurance/goal.png',
      };

    case hasFamilyOopMax(insuranceDetails) &&
      hasHitIndividualDeductible(insuranceDetails) &&
      !hasHitFamilyDeductible(insuranceDetails) &&
      hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      !hasHitFamilyOopMax(insuranceDetails):
      return {
        title: getLimitHitCopy(individualOopMaxData.limit, false),
        subtext: getMonthsUntilPlanExpiresCopy(insuranceDetails, planStartDate, now),
        icon: '/images/insurance/goal.png',
      };

    case hasFamilyOopMax(insuranceDetails) &&
      !hasHitIndividualInNetworkOopMax(insuranceDetails) &&
      hasHitFamilyOopMax(insuranceDetails):
      return {
        title: (
          <span>
            You hit your family <br />
            out-of-pocket maximum
          </span>
        ),
        subtext: getMonthsUntilPlanExpiresCopy(insuranceDetails, planStartDate, now, true),
        icon: '/images/insurance/goal.png',
      };

    default:
      return null;
  }
};

export const getInsuranceHeadlineTransitionTexts = (firstName: any) => [
  `Loading ${firstName}'s plan details...`,
  `Updating ${firstName}'s deductible status...`,
  'Checking for benefit updates...',
  'Double checking plan status...',
  `Loading ${firstName}'s free and discounted benefits...`,
];

const PLAN_TYPES_WHITELIST = [
  'ppo',
  'hmo',
  'pos',
  'epo',
  'commercial',
  'exclusive_provider_organization',
  'group_policy',
];
export const isWhitelistedPlanType = (planType: any) =>
  planType && PLAN_TYPES_WHITELIST.includes(planType.toLowerCase());

export const getPlanTypeOrEmptyString = (insuranceDetails: any): string =>
  insuranceDetails?.plan?.type || '';

const sanitizePlanName = (sentence: any) => {
  if (!sentence) return null;
  return sentence
    .split(/\s+/)
    .map((w: any) => (PLAN_TYPES_WHITELIST.includes(w.toLowerCase()) ? w.toUpperCase() : w))
    .join(' ');
};

export const formatPlanNameForDisplay = (planName: any) => sanitizePlanName(capitalCase(planName));

export const shouldShowSwimlanes = ({
  detailsResult,
  insuranceDetailsSubmitting,
  insuranceProfile,
  npi,
}: any) => {
  const planType = safeGet(detailsResult)('plan.type') || safeGet(insuranceProfile)('insurer_type');
  return (
    npi &&
    !npi.submitting &&
    !insuranceDetailsSubmitting &&
    !isEmpty(insuranceProfile) &&
    !isEmpty(detailsResult) &&
    safeGet(detailsResult, false)('active') &&
    hasSufficientDataToDisplayInsuranceProfile(insuranceProfile) &&
    isWhitelistedPlanType(planType)
  );
};

export const getCopayCoinsuranceBenefitStates = (
  insuranceDetails: any,
  providerType: any,
  isInNetwork: any
) => {
  const network = isInNetwork ? IN_NETWORK_KEY : OUT_OF_NETWORK_KEY;
  const copayValue = getCopay(insuranceDetails, providerType, network);
  const coinsuranceValue = getCoinsurance(insuranceDetails, providerType, network);

  if (!isEmpty(copayValue) && copayValue !== 0 && coinsuranceValue > 0) {
    return {
      [InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE]: `${formatPrice(
        copayValue
      )} + ${formatPercentage(coinsuranceValue)} of bill`,
      [InsuranceBenefitStateTypes.MET_DEDUCTIBLE]: `${formatPrice(copayValue)} + ${formatPercentage(
        coinsuranceValue
      )} of bill`,
      [InsuranceBenefitStateTypes.MET_OOP_MAX]: '$0',
    };
  }

  if (!isEmpty(copayValue) && copayValue !== 0) {
    return {
      [InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE]: `${formatPrice(copayValue)} copay`,
      [InsuranceBenefitStateTypes.MET_DEDUCTIBLE]: `${formatPrice(copayValue)} copay`,
      [InsuranceBenefitStateTypes.MET_OOP_MAX]: '$0 copay',
    };
  }

  if (!isEmpty(coinsuranceValue) && coinsuranceValue !== 0) {
    return {
      [InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE]: '100% of bill',
      [InsuranceBenefitStateTypes.MET_DEDUCTIBLE]: `${formatPercentage(coinsuranceValue)} of bill`,
      [InsuranceBenefitStateTypes.MET_OOP_MAX]: '0% of bill',
    };
  }

  return {};
};

export const useCopayCoinsuranceStepCopy = (
  insuranceDetails: any,
  providerType: any,
  isInNetwork: any
) => {
  return useMemo(() => {
    const insuranceBenefitStates = getCopayCoinsuranceBenefitStates(
      insuranceDetails,
      providerType,
      isInNetwork
    );

    if (hasNoIndividualDeductible(insuranceDetails)) {
      if (hasHitIndividualInNetworkOopMax(insuranceDetails)) {
        return {
          card: [
            {
              title: 'Since OOP max hit',
              value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
              isCurrentStep: true,
            },
            {
              title: 'Until plan resets',
              value: '',
            },
          ],
          modal: [
            {
              title: '1. Before you hit your OOP max...',
              value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
            },
            {
              title: "2. Since you've hit your OOP max...",
              value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
              isCurrentStep: true,
            },
          ],
        };
      }

      return {
        card: [
          {
            title: 'Since no deductible',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
            isCurrentStep: true,
          },
          {
            title: 'After OOP max hit',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
          },
        ],
        modal: [
          {
            title: '1. Since your plan has no deductible...',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
            isCurrentStep: true,
          },
          {
            title: '2. After you hit your OOP max...',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
          },
        ],
      };
    }

    if (hasHitIndividualInNetworkOopMax(insuranceDetails)) {
      return {
        card: [
          {
            title: 'Since OOP max hit',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
            isCurrentStep: true,
          },
          {
            title: 'Until plan resets',
            value: '',
          },
        ],
        modal: [
          {
            title: '1. Before you hit your deductible...',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE],
          },
          {
            title: '2. After you hit your deductible...',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
          },
          {
            title: "3. Since you've hit your OOP max...",
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
            isCurrentStep: true,
          },
        ],
      };
    }

    if (hasHitIndividualDeductible(insuranceDetails)) {
      return {
        card: [
          {
            title: 'Since deductible hit',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
            isCurrentStep: true,
          },
          {
            title: 'After OOP max hit',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
          },
        ],
        modal: [
          {
            title: '1. Before you hit your deductible...',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE],
          },
          {
            title: "2. Since you've hit your deductible...",
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
            isCurrentStep: true,
          },
          {
            title: '3. After you hit your OOP max...',
            value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
          },
        ],
      };
    }

    return {
      card: [
        {
          title: 'Before deductible hit',
          value: insuranceBenefitStates[InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE],
          isCurrentStep: true,
        },
        {
          title: 'After deductible hit',
          value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
        },
      ],
      modal: [
        {
          title: "1. Since you haven't hit your deductible...",
          value: insuranceBenefitStates[InsuranceBenefitStateTypes.UNMET_DEDUCTIBLE],
          isCurrentStep: true,
        },
        {
          title: '2. After you hit your deductible...',
          value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_DEDUCTIBLE],
        },
        {
          title: '3. After you hit your OOP max...',
          value: insuranceBenefitStates[InsuranceBenefitStateTypes.MET_OOP_MAX],
        },
      ],
    };
  }, [insuranceDetails, providerType, isInNetwork]);
};

export * as insuranceDetailsUtils from './insuranceDetails';
